/**
 * Every file on the case — the client's files and order sheets, the
 * partners' returns, the providers' files, the analyst's own uploads — with
 * where each came from and the entry the model wrote for it (its key, `F1`…,
 * is how the agents and the Assistant name it); last, the workbench's own
 * file: the partner's form as the case agent pre-filled it, which has no
 * key and is never entered. A file opens from here, entered (by key) or not
 * yet (by its id); a record drawn from a file opens it from the Evidence
 * view. Files from the analyst go up from here (05 §1, decision 10): they
 * land at once, without a key until the next run enters them.
 */

import { useState } from 'react';

import {
  Anchor,
  Badge,
  Button,
  FileButton,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { CONVERSATION_ATTACHMENT_MIME } from '@/types/attachment';
import { fileDescription, type WorkbenchFile, type WorkbenchUpload } from '@/types/workbench';
import { toError } from '@/utils/errors';
import { fileKindLabel, formatBytes } from './format';

/** The most files one upload takes — the API's limit. */
export const MAX_UPLOAD_FILES = 5;
/** What the case takes: the chat's kinds, archives included (unpacked). */
export const UPLOAD_MIME = CONVERSATION_ATTACHMENT_MIME.join(',');

interface FilesViewProps {
  reportId: string;
  files: WorkbenchFile[] | null;
  error: Error | null;
  /** The file a link names, by key: outlined. */
  focusedKey?: string | null;
  /** Given, the view takes uploads; called with what the upload made. */
  onUploaded?: (made: WorkbenchUpload) => void | Promise<void>;
}

/** Open a file's presigned link in a new tab. */
export async function openWorkbenchFile(reportId: string, key: string): Promise<void> {
  try {
    const link = await vnextApiClient.getWorkbenchFileLink(reportId, key);
    window.open(link.url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    Sentry.captureException(toError(err));
  }
}

/** Open a file not yet entered — or the file an ask was answered with — by
 * its attachment id, in a new tab. */
export async function openWorkbenchAttachment(
  reportId: string,
  attachmentId: string
): Promise<void> {
  try {
    const link = await vnextApiClient.getWorkbenchAttachmentLink(reportId, attachmentId);
    window.open(link.url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    Sentry.captureException(toError(err));
  }
}

/** Open a pre-filled form's presigned link in a new tab. */
export async function openWorkbenchPrefill(reportId: string, prefillId: string): Promise<void> {
  try {
    const link = await vnextApiClient.getWorkbenchPrefillFile(reportId, prefillId);
    window.open(link.url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    Sentry.captureException(toError(err));
  }
}

function when(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';
}

/** How a row is opened: the pre-fill by its id, an entered file by its
 * key, any other attachment by its id. */
function handleOf(file: WorkbenchFile): string | null {
  return file.prefillId ?? file.key ?? file.conversationAttachmentId;
}

function testIdOf(file: WorkbenchFile): string | undefined {
  if (file.key) {
    return `file-${file.key}`;
  }
  if (file.prefillId) {
    return 'file-prefill';
  }
  return file.conversationAttachmentId ? `file-att-${file.conversationAttachmentId}` : undefined;
}

export function FilesView({
  reportId,
  files,
  error,
  focusedKey = null,
  onUploaded,
}: Readonly<FilesViewProps>) {
  const [opening, setOpening] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (picked: File[]) => {
    if (picked.length === 0 || !onUploaded) {
      return;
    }
    if (picked.length > MAX_UPLOAD_FILES) {
      notifications.show({
        color: 'red',
        message: `At most ${MAX_UPLOAD_FILES} files go up at once.`,
      });
      return;
    }
    setUploading(true);
    try {
      const made = await vnextApiClient.uploadWorkbenchFiles(reportId, picked);
      notifications.show({
        color: 'teal',
        message: `${picked.length} file${picked.length === 1 ? '' : 's'} uploaded — entered at the next run.`,
      });
      await onUploaded(made);
    } catch (err) {
      Sentry.captureException(toError(err));
      notifications.show({
        color: 'red',
        message: toError(err, 'Could not upload the files').message,
      });
    } finally {
      setUploading(false);
    }
  };

  const uploader = onUploaded ? (
    <FileButton multiple accept={UPLOAD_MIME} onChange={(picked) => void upload(picked)}>
      {(props) => (
        <Button
          {...props}
          size="xs"
          variant="default"
          leftSection={<IconUpload size={14} />}
          loading={uploading}
          data-testid="files-upload"
        >
          Upload files
        </Button>
      )}
    </FileButton>
  ) : null;

  if (error && !files) {
    return (
      <Text size="sm" c="dimmed">
        The files couldn&apos;t be loaded.
      </Text>
    );
  }
  if (!files) {
    return <Loader size="sm" />;
  }
  if (files.length === 0) {
    return (
      <Group justify="space-between" align="center" data-testid="files-view">
        <Text size="sm" c="dimmed" data-testid="files-empty">
          No files on this case yet.
        </Text>
        {uploader}
      </Group>
    );
  }
  const entered = files.filter((f) => f.key !== null).length;
  // The pre-filled form is never entered: it is the case's output.
  const enterable = files.filter((f) => f.prefillId === null).length;

  const open = async (file: WorkbenchFile) => {
    const handle = handleOf(file);
    if (!handle) {
      return;
    }
    setOpening(handle);
    try {
      if (file.prefillId) {
        await openWorkbenchPrefill(reportId, file.prefillId);
      } else if (file.key) {
        await openWorkbenchFile(reportId, file.key);
      } else {
        await openWorkbenchAttachment(reportId, handle);
      }
    } finally {
      setOpening(null);
    }
  };

  return (
    <Stack gap="sm" data-testid="files-view">
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed">
          {files.length} file{files.length === 1 ? '' : 's'} · {entered} entered by the model
          {entered < enterable ? ' (the rest are entered at the next run)' : ''}
        </Text>
        {uploader}
      </Group>
      <Table verticalSpacing={6} fz="sm" data-testid="files-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={56}>Key</Table.Th>
            <Table.Th>File</Table.Th>
            <Table.Th>What it is</Table.Th>
            <Table.Th w={110}>From</Table.Th>
            <Table.Th w={130}>Added</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {files.map((file) => {
            const description = fileDescription(file);
            const size = formatBytes(file.sizeBytes);
            const focused = file.key !== null && file.key === focusedKey;
            const handle = handleOf(file);
            const openId = file.key
              ? `file-open-${file.key}`
              : file.prefillId
                ? 'file-open-prefill'
                : `file-open-att-${handle ?? ''}`;
            return (
              <Table.Tr
                key={`${file.key ?? ''}:${file.prefillId ?? ''}:${file.conversationAttachmentId ?? ''}:${file.filename}:${file.createdAt ?? ''}`}
                data-testid={testIdOf(file)}
                style={focused ? { background: 'var(--mantine-color-blue-light)' } : undefined}
              >
                <Table.Td>
                  <Text size="xs" fw={600} c="dimmed">
                    {file.key ?? '—'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="wrap">
                    {handle ? (
                      <Anchor
                        component="button"
                        type="button"
                        size="sm"
                        onClick={() => void open(file)}
                        data-testid={openId}
                      >
                        {file.filename}
                      </Anchor>
                    ) : (
                      <Text size="sm">{file.filename}</Text>
                    )}
                    {handle && opening === handle ? <Loader size={12} /> : null}
                    {size ? (
                      <Text size="xs" c="dimmed">
                        {size}
                      </Text>
                    ) : null}
                    {file.superseded ? (
                      <Badge size="xs" variant="outline" color="gray">
                        Superseded
                      </Badge>
                    ) : null}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c={description ? undefined : 'dimmed'}>
                    {description ?? (file.key ? 'No description.' : 'Not entered yet.')}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="xs" variant="light" color="gray" style={{ textTransform: 'none' }}>
                    {fileKindLabel(file.kind)}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {when(file.createdAt)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
